import React, { useState, useEffect } from 'react';
import { loadDefinitions } from '../store/assetDefinitions';
import GridTariffRefField from './GridTariffRefField';

const PropertiesPanel = ({ node, onUpdate, onClose }) => {
  const [editData, setEditData] = useState({});
  const [definition, setDefinition] = useState(null);

  // Load all asset definitions once (cheap; already cached by browser)
  useEffect(() => {
    let cancelled = false;
    loadDefinitions().then((defs) => {
      if (cancelled) return;
      if (node && node.data.assetType) {
        const def = defs.find((d) => d.id === node.data.assetType);
        setDefinition(def || null);
      } else {
        setDefinition(null);
      }
    });
    return () => { cancelled = true; };
  }, [node]);

  useEffect(() => {
    if (node) {
      // Spread properties first, then overlay the hoisted label/status so
      // they always take precedence over any stale value stuck in properties.
      setEditData({
        ...(node.data.properties || {}),
        label: node.data.label || '',
        status: node.data.status || 'active',
      });
    }
  }, [node]);

  if (!node) return null;

  const handleChange = (key, value) => {
    setEditData((prev) => ({ ...prev, [key]: value }));
  };

  // When a grid_tariff_ref field resolves to a real tariff, auto-update the
  // node label and a few derived fields so the canvas shows a meaningful name
  // without the user having to retype it. Manual edits to label afterwards
  // win — this only fires on tariff selection.
  const handleTariffResolved = (fieldName, tariffId, tariffMeta) => {
    setEditData((prev) => {
      const next = { ...prev, [fieldName]: tariffId };
      if (tariffMeta) {
        const dir = prev.direction || 'import';
        next.label = `${tariffMeta.utility_name} · ${tariffMeta.code} · ${dir}`;
      }
      return next;
    });
  };

  const handleSave = () => {
    const { label, status, ...properties } = editData;
    onUpdate(node.id, { label, status, properties });
  };

  const handleAddProperty = () => {
    const key = prompt('Property name:');
    if (key && key.trim()) {
      setEditData((prev) => ({ ...prev, [key.trim()]: '' }));
    }
  };

  // Field names declared by the asset definition — used to render schema-aware
  // inputs and to decide which properties fall back to free-form text.
  const declaredFields = (definition && definition.fields) || [];
  const declaredNames = new Set(declaredFields.map((f) => f.name));
  // `status` and `label` are hoisted into their own editors at the top of the
  // panel, so skip them if the definition also declares them.
  const isReservedField = (name) => name === 'status' || name === 'label';

  const renderFieldInput = (field, currentValue) => {
    const val = currentValue == null ? '' : currentValue;
    switch (field.type) {
      case 'number':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="number"
              value={val}
              onChange={(e) => handleChange(field.name, e.target.value)}
              style={{ flex: 1 }}
            />
            {field.unit && <span style={{ fontSize: '12px', color: '#94a3b8' }}>{field.unit}</span>}
          </div>
        );
      case 'select':
        return (
          <select value={val} onChange={(e) => handleChange(field.name, e.target.value)}>
            {(field.options || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={val === true || val === 'true'}
            onChange={(e) => handleChange(field.name, e.target.checked)}
          />
        );
      case 'grid_tariff_ref':
        return (
          <GridTariffRefField
            value={val}
            direction={editData.direction || 'import'}
            onChange={(v, meta) => handleTariffResolved(field.name, v, meta)}
          />
        );
      case 'text':
      default:
        return (
          <input
            type="text"
            value={val}
            onChange={(e) => handleChange(field.name, e.target.value)}
          />
        );
    }
  };

  // Free-form extras: properties present on the node that aren't declared by
  // the definition. Render them as plain text inputs so nothing gets lost when
  // an older asset predates a schema change.
  const extras = Object.entries(editData).filter(
    ([key]) => !['label', 'status'].includes(key) && !declaredNames.has(key)
  );

  return (
    <div className="properties-panel">
      <div className="properties-header">
        <span>{node.data.icon} {node.data.label}</span>
        <button className="properties-close" onClick={onClose}>✕</button>
      </div>

      <div className="properties-body">
        <div className="property-group">
          <label>Label</label>
          <input
            type="text"
            value={editData.label || ''}
            onChange={(e) => handleChange('label', e.target.value)}
          />
        </div>

        <div className="property-group">
          <label>Status</label>
          <select
            value={editData.status || 'active'}
            onChange={(e) => handleChange('status', e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Maintenance</option>
            <option value="planned">Planned</option>
          </select>
        </div>

        <hr />
        <h4>Properties</h4>

        {declaredFields
          .filter((f) => !isReservedField(f.name))
          .map((field) => (
            <div key={field.id || field.name} className="property-group">
              <label>
                {field.label || field.name}
                {field.required && <span style={{ color: '#f87171' }}> *</span>}
              </label>
              {renderFieldInput(field, editData[field.name])}
            </div>
          ))}

        {extras.map(([key, value]) => (
          <div key={key} className="property-group">
            <label>{key}</label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
            />
          </div>
        ))}

        <button className="btn-add-property" onClick={handleAddProperty}>
          + Add Property
        </button>
      </div>

      <div className="properties-footer">
        <button className="btn-save" onClick={handleSave}>
          Apply Changes
        </button>
      </div>
    </div>
  );
};

export default PropertiesPanel;
